import React, { useState } from 'react'
import {
  GrowthWheelAssessmentComparisonCompetency,
  GrowthWheelAssessmentSession,
} from '../../types/GrowthWheel'
import {
  buildSvgPoints,
  CENTER,
  ChartPoint,
  COLORS,
  getAxisPoint,
  LABEL_DISTANCE,
  LEVELS,
  MAX_SCORE,
  RADIUS,
  wrapLabel,
} from './growthComparisonChartUtils'

type TooltipState = {
  point: ChartPoint
  x: number
  y: number
}

interface Props {
  competencies: GrowthWheelAssessmentComparisonCompetency[]
  pointGroups: ChartPoint[][]
  sessions: GrowthWheelAssessmentSession[]
}

export function GrowthComparisonChartCanvas({
  competencies,
  pointGroups,
  sessions,
}: Props) {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null)

  const getCursorPosition = (event: React.MouseEvent<SVGElement>) => {
    const chartWrap = event.currentTarget.closest(
      '.gw-growth-comparison-chart-wrap',
    )
    const rect = chartWrap?.getBoundingClientRect()

    if (!rect) return { x: 0, y: 0 }

    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    }
  }

  const getSvgCursorPosition = (event: React.MouseEvent<SVGElement>) => {
    const svg = event.currentTarget.ownerSVGElement
    const matrix = svg?.getScreenCTM()

    if (!svg || !matrix) return null

    const cursor = svg.createSVGPoint()
    cursor.x = event.clientX
    cursor.y = event.clientY
    return cursor.matrixTransform(matrix.inverse())
  }

  const getNearestPoint = (
    points: ChartPoint[],
    event: React.MouseEvent<SVGElement>,
  ) => {
    const cursor = getSvgCursorPosition(event)
    if (!cursor) return points[0]

    return points.reduce((nearest, point) => {
      const nearestDistance = Math.hypot(
        nearest.x - cursor.x,
        nearest.y - cursor.y,
      )
      const pointDistance = Math.hypot(point.x - cursor.x, point.y - cursor.y)
      return pointDistance < nearestDistance ? point : nearest
    }, points[0])
  }

  const showTooltip = (
    point: ChartPoint,
    event: React.MouseEvent<SVGElement>,
  ) => {
    setTooltip({
      point,
      ...getCursorPosition(event),
    })
  }

  const showNearestTooltip = (
    points: ChartPoint[],
    event: React.MouseEvent<SVGElement>,
  ) => {
    showTooltip(getNearestPoint(points, event), event)
  }

  return (
    <div className="gw-growth-comparison-chart-wrap">
      <svg
        className="gw-growth-comparison-chart"
        viewBox="0 0 360 360"
        role="img"
        aria-label="Spider graph comparing competency levels by assessment cycle"
      >
        {LEVELS.map(level => {
          const gridPoints = competencies.map((_competency, index) =>
            getAxisPoint(index, competencies.length, (RADIUS * level) / 5),
          )

          return (
            <polygon
              key={level}
              points={buildSvgPoints(gridPoints)}
              className="gw-growth-comparison-grid"
            />
          )
        })}

        {LEVELS.map(level => (
          <text
            key={`level-${level}`}
            x={CENTER + 1}
            y={CENTER - (RADIUS * level) / MAX_SCORE}
            className="gw-growth-comparison-radius-label"
            textAnchor="middle"
            dominantBaseline="middle"
          >
            {level}
          </text>
        ))}

        {competencies.map((competency, index) => {
          const axis = getAxisPoint(index, competencies.length)
          const label = getAxisPoint(index, competencies.length, LABEL_DISTANCE)
          const labelLines = wrapLabel(competency.name)

          return (
            <g key={competency.name}>
              <line
                x1={CENTER}
                y1={CENTER}
                x2={axis.x}
                y2={axis.y}
                className="gw-growth-comparison-axis"
              />
              <text
                x={label.x}
                y={label.y}
                className="gw-growth-comparison-label"
                textAnchor="middle"
                dominantBaseline="middle"
              >
                {labelLines.map((line, lineIndex) => (
                  <tspan
                    key={line}
                    x={label.x}
                    dy={
                      lineIndex === 0
                        ? `${-(labelLines.length - 1) * 6}px`
                        : '12px'
                    }
                  >
                    {line}
                  </tspan>
                ))}
              </text>
            </g>
          )
        })}

        {pointGroups.map((points, sessionIndex) => (
          <g key={sessions[sessionIndex]?.id}>
            <polygon
              points={buildSvgPoints(points)}
              fill={COLORS[sessionIndex]}
              className="gw-growth-comparison-area"
              onMouseMove={event => showNearestTooltip(points, event)}
              onMouseLeave={() => setTooltip(null)}
            />
            <polygon
              points={buildSvgPoints(points)}
              stroke={COLORS[sessionIndex]}
              className="gw-growth-comparison-line"
            />
            <polygon
              points={buildSvgPoints(points)}
              className="gw-growth-comparison-line-hit-target"
              onMouseMove={event => showNearestTooltip(points, event)}
              onMouseLeave={() => setTooltip(null)}
            />
            {tooltip &&
              points.some(
                point =>
                  point.sessionName === tooltip.point.sessionName &&
                  point.competencyName === tooltip.point.competencyName,
              ) && (
                <circle
                  cx={tooltip.point.x}
                  cy={tooltip.point.y}
                  r="4.5"
                  fill={COLORS[sessionIndex]}
                  className="gw-growth-comparison-hover-point"
                />
              )}
            {points.map(point => (
              <circle
                key={`${point.sessionName}-${point.competencyName}`}
                cx={point.x}
                cy={point.y}
                r="8"
                className="gw-growth-comparison-hit-target"
                onMouseMove={event => showTooltip(point, event)}
                onMouseLeave={() => setTooltip(null)}
                onFocus={() =>
                  setTooltip({
                    point,
                    x: point.x,
                    y: point.y,
                  })
                }
                onBlur={() => setTooltip(null)}
                tabIndex={0}
              >
                <title>
                  {point.sessionName} - {point.competencyName}: {point.score}
                </title>
              </circle>
            ))}
          </g>
        ))}
      </svg>

      {tooltip && (
        <div
          className="gw-growth-comparison-tooltip"
          style={{
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div className="gw-growth-comparison-tooltip__title">
            {tooltip.point.competencyName}
          </div>
          <div>{tooltip.point.sessionName}</div>
          <strong>{tooltip.point.score}</strong>
        </div>
      )}

      <div className="gw-growth-comparison-legend">
        {sessions.map((session, index) => (
          <div key={session.id} className="gw-growth-comparison-legend__item">
            <span
              className="gw-growth-comparison-legend__line"
              style={{
                backgroundColor: COLORS[index],
              }}
            />
            <span>{session.name}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
